import { Fragment, useState } from 'react'

import { api } from '../api/client'
import {
  Card,
  EmptyState,
  ErrorBanner,
  etichettaStato,
  formatDateTime,
  formatDurata,
  Spinner,
  StatusBadge,
  timeAgo,
} from '../components/common'
import { DataTable } from '../components/DataTable'
import DiscoveryForm from '../components/DiscoveryForm'
import type { Mode } from '../components/DiscoveryForm'
import JobReport from '../components/JobReport'
import { ModuloInVista } from '../components/ModuloInVista'
import type {
  Credential,
  CredentialGroup,
  DiscoveryKind,
  DiscoveryOptimization,
  OptimizationProposal,
  ScheduleDraft,
} from '../api/types'
import { useAsync, usePolling } from '../hooks/useAsync'
import { t } from '../i18n/lingua'

/** Il tetto ai job di una finestra (lo stesso del Control Plane). */
const TETTO_JOB = 5000

/** Dove il browser ricorda «Mostra solo job scansioni». */
const CHIAVE_SOLO_SCANSIONI = 'censo.jobs.soloScansioni'

/** Scansioni fuori dal catalogo dei riquadri (le avvia un piano, non un clic). */
const SCANSIONI_FUORI_CATALOGO = new Set(['snmp_deep'])

/** Riepilogo compatto degli avvisi di un job, leggibile dall'elenco. */
function EventSummary({ counts }: { counts: Record<string, number> }) {
  const errors = counts.error ?? 0
  const warnings = counts.warning ?? 0
  if (!errors && !warnings) return <span className="text-muted">—</span>
  return (
    <>
      {errors > 0 && (
        <span className="badge badge--error">
          {errors === 1 ? t('1 errore') : t('{n} errori', { n: errors })}
        </span>
      )}{' '}
      {warnings > 0 && (
        <span className="badge badge--warn">
          {warnings === 1 ? t('1 avviso') : t('{n} avvisi', { n: warnings })}
        </span>
      )}
    </>
  )
}

/**
 * I job di scansione, raggruppati per famiglia.
 *
 * Un elenco piatto di undici tipi mescola gesti diversi — «devo inventariare la
 * rete», «devo inventariare AWS» — e costringe a leggerli tutti per trovarne
 * uno. Le famiglie vengono dal catalogo del Control Plane, non da un elenco
 * scritto qui: un elenco locale si disallineerebbe al primo connettore aggiunto.
 *
 * Ogni tipo porta le due sole cose che si possono fare con esso: **avviarlo
 * ora** o **pianificarlo**. Sono la stessa configurazione con due esiti, quindi
 * un modulo solo (vedi DiscoveryForm).
 */
export default function Jobs() {
  const [openJobId, setOpenJobId] = useState<string | null>(null)
  const [open, setOpen] = useState<{ kind: DiscoveryKind; mode: Mode } | null>(null)
  // Quale categoria di job mostrare: '' = tutte. Come la riga di scelta delle
  // Credenziali, riduce lo scorrimento quando le famiglie sono tante.
  const [filtroFamiglia, setFiltroFamiglia] = useState('')
  const [notice, setNotice] = useState<string | null>(null)
  // La scansione da cui ripianificare, già riletta come modulo compilato.
  const [draft, setDraft] = useState<ScheduleDraft | null>(null)
  const [draftError, setDraftError] = useState<unknown>(null)
  const [draftBusy, setDraftBusy] = useState<string | null>(null)
  const [retryBusy, setRetryBusy] = useState<string | null>(null)

  async function retry(jobId: string) {
    setRetryBusy(jobId)
    setDraftError(null)
    try {
      await api.retryJob(jobId)
      setNotice(t('Job rilanciato: riparte con la stessa configurazione.'))
      void jobs.reload()
    } catch (err) {
      setDraftError(err)
    } finally {
      setRetryBusy(null)
    }
  }

  async function planFrom(jobId: string) {
    if (draft?.job_id === jobId) {
      setDraft(null)
      return
    }
    setDraftBusy(jobId)
    setDraftError(null)
    try {
      setDraft(await api.scheduleDraft(jobId))
    } catch (err) {
      setDraftError(err)
    } finally {
      setDraftBusy(null)
    }
  }

  // La finestra dei job: fino a 30 giorni. L'elenco intero della finestra
  // arriva alla tabella (filtri per colonna e pagine lato console), quindi si
  // riguarda meno spesso quanto più è lunga — 30 giorni ogni 5 secondi sarebbe
  // un peso per niente, e i job in corso stanno comunque in cima.
  const [giorni, setGiorni] = useState(7)
  // Solo le scansioni (i tipi dei riquadri qui sopra): le consegne degli
  // agenti d'inventario, l'autoinventario della sonda ogni 12 ore e i job di
  // servizio (campioni di traffico, diagnosi dei flussi) sommergono l'elenco.
  // Attiva di default; la scelta resta nel browser di chi guarda.
  const [soloScansioni, setSoloScansioni] = useState<boolean>(() => {
    try {
      return localStorage.getItem(CHIAVE_SOLO_SCANSIONI) !== '0'
    } catch {
      return true
    }
  })
  function cambiaSoloScansioni(valore: boolean) {
    setSoloScansioni(valore)
    try {
      localStorage.setItem(CHIAVE_SOLO_SCANSIONI, valore ? '1' : '0')
    } catch {
      // Niente memoria del browser: la scelta vale finché la pagina è aperta.
    }
  }
  const jobs = usePolling(
    () => api.jobs(undefined, { days: giorni, limit: TETTO_JOB }),
    giorni <= 1 ? 5000 : giorni <= 7 ? 15000 : 30000,
    [giorni],
  )

  const credentials = useAsync(() => api.credentials(), [])
  const credentialGroups = useAsync(() => api.credentialGroups(), [])
  const kinds = useAsync(() => api.discoveryKinds(), [])
  const tipiScansione = new Set([...(kinds.data ?? []).map((k) => k.kind), ...Array.from(SCANSIONI_FUORI_CATALOGO)])
  const eScansione = (kind: string) =>
    kinds.data ? tipiScansione.has(kind) : kind !== 'inventory_agent'
  const righeJob = (jobs.data ?? []).filter((j) => !soloScansioni || eScansione(j.kind))
  const altriNascosti = (jobs.data?.length ?? 0) - righeJob.length

  // Ordine delle famiglie come le dichiara il catalogo, non alfabetico: la rete
  // viene prima perché è ciò che si scansiona per primo in un'installazione.
  // Le famiglie che hanno una pagina propria non compaiono qui.
  //
  // L'elenco è **una costante di questa pagina** e non il nome di un tipo: se
  // domani arriva una seconda sorgente di sicurezza, va dove deve andare da
  // sola. Scrivere «ntopng» avrebbe funzionato oggi e avrebbe fatto comparire
  // la prossima nel posto sbagliato, senza che niente lo segnali.
  const ALTROVE = new Set(['security'])

  const families: { key: string; label: string; kinds: DiscoveryKind[] }[] = []
  for (const entry of kinds.data ?? []) {
    if (ALTROVE.has(entry.family)) continue
    let family = families.find((f) => f.key === entry.family)
    if (!family) {
      family = { key: entry.family, label: entry.family_label, kinds: [] }
      families.push(family)
    }
    family.kinds.push(entry)
  }

  const noCredentials = credentials.data?.length === 0

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Job scansioni')}</h1>
          <p className="page__subtitle">
            {t('Avvia la discovery associando al job un piano e un Pool di asset. La sonda esegue la sequenza configurata sui target del pool, acquisisce i risultati e aggiorna i Configuration Item. Titolo e descrizione identificano finalità e contesto.')}
          </p>
        </div>
      </header>

      <ErrorBanner error={jobs.error ?? kinds.error ?? draftError} />
      {notice && <div className="banner banner--ok">{notice}</div>}

      {noCredentials && (
        <div className="banner banner--warn">
          {t("Nessuna credenziale configurata: creane una nella sezione Credenziali, altrimenti non c'è modo di interrogare nulla.")}
        </div>
      )}

      {kinds.loading && !kinds.data ? (
        <Spinner />
      ) : (
        // Etichette e suggerimenti del catalogo arrivano dall'API in italiano,
        // ed è lì che devono stare: sono la descrizione di cosa fa la
        // piattaforma, non testo di questa pagina. Passano da `t` come tutto il
        // resto — la frase italiana è la chiave — e la guardia delle traduzioni
        // le raccoglie dal catalogo Python esattamente come raccoglie i modelli
        // dei rapporti dalla sonda.
        <>
          {families.length > 1 && (
            <div className="report__filters" role="tablist" aria-label={t('Categoria')}>
              <button
                type="button"
                className={filtroFamiglia === '' ? 'chip chip--active' : 'chip'}
                onClick={() => setFiltroFamiglia('')}
              >
                {t('Tutte')} ({families.reduce((n, f) => n + f.kinds.length, 0)})
              </button>
              {families.map((f) => (
                <button
                  type="button"
                  key={f.key}
                  className={filtroFamiglia === f.key ? 'chip chip--active' : 'chip'}
                  onClick={() => setFiltroFamiglia(f.key)}
                >
                  {t(f.label)} ({f.kinds.length})
                </button>
              ))}
            </div>
          )}
          {families
            .filter((family) => !filtroFamiglia || family.key === filtroFamiglia)
            .map((family) => (
          <Card title={t(family.label)} key={family.key}>
            <div className="kindgrid">
              {family.kinds.map((entry) => (
                <Fragment key={entry.kind}>
                <div
                  className={`kindcard${open?.kind.kind === entry.kind ? ' kindcard--open' : ''}`}
                >
                  <div className="kindcard__head">
                    <span className="kindcard__name">{t(entry.label)}</span>
                    <Availability entry={entry} />
                  </div>
                  <p className="kindcard__hint">{t(entry.hint)}</p>
                  <div className="kindcard__actions">
                    <button
                      className="secondary"
                      disabled={!entry.licensed}
                      onClick={() =>
                        setOpen(
                          open?.kind.kind === entry.kind && open.mode === 'run'
                            ? null
                            : { kind: entry, mode: 'run' },
                        )
                      }
                    >
                      {t('Avvia ora')}
                    </button>
                    <button
                      className="secondary"
                      disabled={!entry.licensed}
                      onClick={() =>
                        setOpen(
                          open?.kind.kind === entry.kind && open.mode === 'schedule'
                            ? null
                            : { kind: entry, mode: 'schedule' },
                        )
                      }
                    >
                      {t('Pianifica')}
                    </button>
                  </div>
                </div>
                {/* Il modulo si apre **subito sotto la tessera cliccata**, a
                    tutta larghezza nella griglia, e si porta in vista: in fondo
                    al riquadro, con molti tipi, finiva fuori dallo schermo e il
                    pulsante sembrava non funzionare. */}
                {open?.kind.kind === entry.kind && (
                  <ModuloInVista className="kindform kindform--inline">
                    <h3 className="kindform__title">
                      {open.mode === 'run'
                        ? t('Avvia: {tipo}', { tipo: t(open.kind.label) })
                        : t('Pianifica: {tipo}', { tipo: t(open.kind.label) })}
                    </h3>
                    <DiscoveryForm
                      kind={open.kind}
                      mode={open.mode}
                      credentials={credentials.data ?? []}
                      credentialGroups={credentialGroups.data ?? []}
                      onCancel={() => setOpen(null)}
                      onDone={(message) => {
                        setNotice(message)
                        setOpen(null)
                        jobs.reload()
                      }}
                    />
                  </ModuloInVista>
                )}
              </Fragment>
              ))}
            </div>
          </Card>
            ))}
        </>
      )}

      <OttimizzaDiscovery />

      <Card
        title={t('Job recenti')}
        actions={
          <>
          <label className="checkbox" style={{ margin: '0 12px 0 0' }}>
            <input type="checkbox" checked={soloScansioni} onChange={(e) => cambiaSoloScansioni(e.target.checked)} />
            {t('Mostra solo job scansioni')}
          </label>
          <label className="jobs__finestra">
            {t('Finestra')}{' '}
            <select value={giorni} onChange={(e) => setGiorni(Number(e.target.value))}>
              <option value={1}>{t('Ultime 24 ore')}</option>
              <option value={7}>{t('Ultimi 7 giorni')}</option>
              <option value={30}>{t('Ultimi 30 giorni')}</option>
            </select>
          </label>
          </>
        }
      >
        {jobs.loading && !jobs.data ? (
          <Spinner />
        ) : !righeJob.length ? (
          <EmptyState
            title={t('Nessun job')}
            hint={altriNascosti > 0
              ? t('{n} job che non sono scansioni sono nascosti: togli la spunta «Mostra solo job scansioni» per vederli.', { n: altriNascosti })
              : t('Avvia la prima scansione dai riquadri qui sopra.')}
          />
        ) : (
          <>
            {(jobs.data?.length ?? 0) >= TETTO_JOB && (
              <div className="banner banner--warn">
                {t('Nella finestra ci sono più di {n} job: sono mostrati i {n} più recenti. Restringi la finestra per vedere il resto.', { n: TETTO_JOB })}
              </div>
            )}
            <DataTable
              rows={righeJob}
              rowKey={(j) => j.id}
              initialSort={{ key: 'created', dir: 'desc' }}
              conteggio={{ nome: t('job') }}
              paginazione={{ predefinita: 25, opzioni: [10, 25, 50, 100, 250] }}
              expanded={(j) => openJobId === j.id || draft?.job_id === j.id}
              expand={(j) =>
                openJobId === j.id ? (
                  <JobReport job={j} onMeta={() => void jobs.reload()} />
                ) : draft?.job_id === j.id ? (
                  <PlanFromJob
                    draft={draft}
                    kind={kinds.data?.find((k) => k.kind === draft.kind)}
                    credentials={credentials.data ?? []}
                    credentialGroups={credentialGroups.data ?? []}
                    onCancel={() => setDraft(null)}
                    onDone={(message) => {
                      setNotice(message)
                      setDraft(null)
                    }}
                  />
                ) : null
              }
              columns={[
                {
                  key: 'kind',
                  header: t('Tipo'),
                  // Etichetta del catalogo (dall'API): passa da `t` come nelle schede sopra.
                  value: (j) => {
                    // L'inventario che la sonda fa di sé stessa: si dice, perché
                    // «inventory_agent» farebbe cercare un agente che non c'è.
                    if (j.spec?.self_inventory) return t('Autoinventario sonda')
                    const etichetta = kinds.data?.find((k) => k.kind === j.kind)?.label
                    return etichetta ? t(etichetta) : j.kind
                  },
                },
                {
                  key: 'title',
                  header: t('Titolo'),
                  value: (j) => j.title ?? '',
                  render: (j) => (
                    <span title={j.description ?? undefined}>{j.title ?? <span className="text-muted">—</span>}</span>
                  ),
                },
                {
                  key: 'status',
                  header: t('Stato'),
                  // Si filtra sulla parola che si vede («Riuscito»), non sul codice.
                  value: (j) => etichettaStato(j.status) ?? j.status,
                  render: (j) => <StatusBadge status={j.status} />,
                },
                {
                  key: 'targets',
                  header: t('Ambito'),
                  mono: true,
                  value: (j) =>
                    Array.isArray(j.spec.targets) && (j.spec.targets as string[]).length
                      ? (j.spec.targets as string[]).join(', ')
                      : '',
                },
                {
                  key: 'esito',
                  header: t('Esito'),
                  // Ordinando, prima chi ha più errori, poi più avvisi.
                  value: (j) => {
                    const c = j.event_counts ?? {}
                    const e = c.error ?? 0
                    const w = c.warning ?? 0
                    return e || w ? `${String(e).padStart(5, '0')}.${String(w).padStart(5, '0')} ${t('{n} errori', { n: e })} ${t('{n} avvisi', { n: w })}` : ''
                  },
                  render: (j) => <EventSummary counts={j.event_counts ?? {}} />,
                },
                {
                  key: 'created',
                  header: t('Creato'),
                  // Il valore è la data ISO: si ordina bene e si filtra per giorno («2026-09-25»).
                  value: (j) => j.created_at ?? '',
                  render: (j) => <span title={formatDateTime(j.created_at)}>{timeAgo(j.created_at)}</span>,
                },
                {
                  key: 'completed',
                  header: t('Completato'),
                  value: (j) => j.completed_at ?? '',
                  render: (j) =>
                    j.completed_at ? <span title={formatDateTime(j.completed_at)}>{timeAgo(j.completed_at)}</span> : '—',
                },
                {
                  key: 'durata',
                  header: t('Durata'),
                  filter: false,
                  value: (j) =>
                    j.started_at
                      ? (new Date(j.completed_at ?? Date.now()).getTime() - new Date(j.started_at).getTime()) / 1000
                      : -1,
                  render: (j) => formatDurata(j.started_at, j.completed_at),
                },
                {
                  key: 'azioni',
                  header: '',
                  filter: false,
                  sort: false,
                  value: () => '',
                  render: (j) => (
                    <span style={{ whiteSpace: 'nowrap' }}>
                      <button
                        type="button"
                        className="link"
                        onClick={() => {
                          setDraft(null)
                          setOpenJobId(openJobId === j.id ? null : j.id)
                        }}
                      >
                        {openJobId === j.id ? t('Chiudi') : t('Rapporto')}
                      </button>{' '}
                      {/* Ripetere una scansione che ha già funzionato è la
                          cosa che si fa più spesso dopo averla guardata. Non per
                          l'autoinventario: lo rifà la sonda da sola ogni 12 ore. */}
                      {!j.spec?.self_inventory && (<>
                      <button
                        type="button"
                        className="link"
                        disabled={draftBusy === j.id}
                        onClick={() => {
                          setOpenJobId(null)
                          void planFrom(j.id)
                        }}
                      >
                        {draft?.job_id === j.id ? t('Chiudi') : t('Pianifica come questa')}
                      </button>{' '}
                      {/* Ritenta: un job nuovo con la stessa configurazione;
                          il verbale del vecchio resta. */}
                      <button
                        type="button"
                        className="link"
                        disabled={retryBusy === j.id}
                        onClick={() => retry(j.id)}
                      >
                        {retryBusy === j.id ? t('Rilancio…') : t('Ritenta')}
                      </button>
                      </>)}
                    </span>
                  ),
                },
              ]}
            />
          </>
        )}
      </Card>
    </>
  )
}

/**
 * Il modulo di pianificazione aperto sotto la scansione da cui nasce.
 *
 * ## Perché sotto la riga e non in cima alla pagina
 *
 * Perché la bozza parla di **quella** scansione, e un modulo che compare
 * altrove costringe a ricordarsi da quale riga viene — che è esattamente il
 * lavoro di memoria che questo pulsante esiste per togliere.
 *
 * ## Perché elenca cosa ha portato
 *
 * Alcuni valori arrivano dalla scansione e **non hanno un campo** in questo
 * modulo — il ritmo di interrogazione è il caso. Portarli in silenzio andrebbe
 * bene finché nessuno se ne accorge, e il giorno che qualcuno confronta la
 * pianificazione con il job non saprebbe da dove viene quel numero. Le note
 * dicono anche il contrario: ciò che **non** si è potuto portare, con il campo
 * lasciato vuoto invece che indovinato.
 */
function PlanFromJob({
  draft,
  kind,
  credentials,
  credentialGroups,
  onCancel,
  onDone,
}: {
  draft: ScheduleDraft
  kind: DiscoveryKind | undefined
  credentials: Credential[]
  credentialGroups: CredentialGroup[]
  onCancel: () => void
  onDone: (message: string) => void
}) {
  if (!kind) {
    // Il tipo di quella scansione non è più in catalogo: succede quando una
    // sonda vecchia ha prodotto un job di un tipo poi rimosso. Dirlo, invece di
    // mostrare un modulo senza opzioni che verrebbe rifiutato al salvataggio.
    return (
      <div className="banner banner--warn">
        {t('Il tipo {tipo} non è più fra quelli disponibili: questa scansione non si può ripianificare così com’è.', { tipo: draft.kind })}
      </div>
    )
  }

  return (
    <div className="kindform">
      <h3 className="kindform__title">{t('Pianifica come: {nome}', { nome: draft.name })}</h3>

      {draft.already_scheduled && (
        <div className="banner banner--warn">
          {t('Questa scansione è nata dalla pianificazione «{nome}». Crearne un’altra uguale raddoppia il carico sulle sonde: ha senso solo se vuoi una ricorrenza diversa.', { nome: draft.already_scheduled.name })}
        </div>
      )}

      {draft.notes.length > 0 && (
        <div className="banner banner--warn">
          <ul style={{ margin: 0, paddingLeft: '1.2em' }}>
            {draft.notes.map((note) => (
              <li key={note}>{note}</li>
            ))}
          </ul>
        </div>
      )}

      <p className="text-muted">
        {t('Ripresi da questa scansione: ambito, credenziali, sonda, opzioni e ritmo di interrogazione ({pps} pacchetti al secondo). Resta da decidere quando.', { pps: draft.rate_limit_pps })}
        {draft.pool_ids.length > 0 &&
          ` ${t('I pool si riprendono per riferimento: la pianificazione seguirà le reti che verranno aggiunte.')}`}
      </p>

      <DiscoveryForm
        kind={kind}
        mode="schedule"
        initial={draft}
        credentials={credentials}
        credentialGroups={credentialGroups}
        onCancel={onCancel}
        onDone={onDone}
      />
    </div>
  )
}

/**
 * Perché un tipo non è utilizzabile.
 *
 * Le due ragioni sono diverse e portano a due azioni diverse: la licenza si
 * acquista, la sonda si collega. Mostrarle entrambe come «non disponibile»
 * lascerebbe a indovinare quale delle due.
 */
function Availability({ entry }: { entry: DiscoveryKind }) {
  if (!entry.licensed) {
    return <span className="badge badge--muted">{t('non compreso nella licenza')}</span>
  }
  if (!entry.probe_available) {
    return <span className="badge badge--warn">{t('nessuna sonda')}</span>
  }
  return <span className="badge badge--ok">{t('pronto')}</span>
}

/**
 * «Analizza e ottimizza discovery».
 *
 * Non riscrive le scansioni: le **propone**. Dai dati di provenienza in CMDB
 * ricava gli asset che rispondono, li raggruppa per come sono stati scoperti e
 * suggerisce un job puntuale al posto di uno sweep cieco; e conta gli asset visti
 * da più fonti, che è lavoro doppio. Trasformare una proposta in un job resta una
 * scelta di chi amministra — con le sue credenziali — quindi qui si mostra e si
 * copiano gli indirizzi, non si crea niente di nascosto.
 */
function OttimizzaDiscovery() {
  const [analisi, setAnalisi] = useState<DiscoveryOptimization | null>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>(null)

  async function analizza() {
    setBusy(true)
    setError(null)
    try {
      setAnalisi(await api.discoveryOptimization())
    } catch (e) {
      setError(e)
    } finally {
      setBusy(false)
    }
  }

  return (
    <Card title={t('Ottimizza discovery')}>
      <p className="text-muted" style={{ marginTop: 0 }}>
        {t('Analizza la provenienza dei dati in CMDB per creare job puntuali sugli indirizzi già rilevati come attivi, raggruppati per origine. Evidenzia inoltre gli asset acquisiti da più scansioni, così da individuare sovrapposizioni nel perimetro di discovery.')}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button onClick={analizza} disabled={busy}>
          {busy ? t('Analisi…') : t('Analizza e ottimizza discovery')}
        </button>
      </div>
      <ErrorBanner error={error} />
      {analisi && <RisultatoOttimizzazione analisi={analisi} />}
    </Card>
  )
}

function RisultatoOttimizzazione({ analisi }: { analisi: DiscoveryOptimization }) {
  if (analisi.indirizzabili === 0) {
    return (
      <EmptyState
        title={t('Ancora niente da ottimizzare')}
        hint={t('Servono asset già in CMDB con un indirizzo di gestione: fai una prima scansione, poi torna qui.')}
      />
    )
  }
  return (
    <div style={{ marginTop: 12 }}>
      <p className="text-muted">
        {t('{n} asset indirizzabili in CMDB.', { n: analisi.indirizzabili })}{' '}
        {analisi.ridondanti.count > 0 && (
          <>
            {t('{n} visti da più fonti (lavoro doppio).', { n: analisi.ridondanti.count })}
          </>
        )}
      </p>
      {analisi.proposte.length === 0 ? (
        <p className="text-muted">
          {t('Nessun gruppo puntuale da proporre: gli asset non provengono da metodi di rete indirizzabili.')}
        </p>
      ) : (
        analisi.proposte.map((p) => <PropostaRiga key={p.metodo} proposta={p} />)
      )}
    </div>
  )
}

function PropostaRiga({ proposta }: { proposta: OptimizationProposal }) {
  const [copiato, setCopiato] = useState(false)

  async function copia() {
    try {
      await navigator.clipboard.writeText(proposta.indirizzi.join(', '))
      setCopiato(true)
      setTimeout(() => setCopiato(false), 2000)
    } catch {
      // Il browser può negare la clipboard: gli indirizzi restano comunque
      // visibili nel dettaglio qui sotto, da selezionare a mano.
    }
  }

  return (
    <div className="panel" style={{ marginBottom: 8 }}>
      <strong>{proposta.label}</strong>{' '}
      <span className="badge badge--info">{proposta.ci_count}</span>
      <p className="text-muted" style={{ margin: '4px 0' }}>
        {proposta.motivo}
      </p>
      <div className="rowactions" style={{ textAlign: 'left' }}>
        <button className="secondary" onClick={copia}>
          {copiato ? t('Copiati') : t('Copia indirizzi')}
        </button>
      </div>
      <details style={{ marginTop: 6 }}>
        <summary className="text-muted">
          {t('Indirizzi')}
          {proposta.troncato && ` ${t('(primi {n})', { n: proposta.indirizzi.length })}`}
        </summary>
        <pre className="mono" style={{ whiteSpace: 'pre-wrap', marginTop: 6 }}>
          {proposta.indirizzi.join(', ')}
        </pre>
      </details>
    </div>
  )
}
